import React from "react";
import BrochureWithText from "../../Brochure/BrochureWithText";
import { errorMessages } from "../../../../constants/errorMessages.constants";
import { typeOfPAF } from "../../../../constants/typeOfPAF";
import { getAge, isDispensingMoreThan3DaysBetween2Dates } from "../../../../shared/helpers";
import {
  checkIfNumberOfCyclesExceeded,
  isNotSelectedAnyCapsulePerCycle,
} from "./patientInitiationValidation";

/**
 * Validates step 5 (digital PAF) before advancing. Returns whether to show errors,
 * open the brochure modal, or proceed.
 */
export function evaluatePatientInitiationStep5({
  areAllFieldsValid,
  registerPatient,
  prescriptionField,
  scheduleDisplayType,
  hcp,
  confirmation_risk_patient,
}) {
  if (!areAllFieldsValid) {
    return { outcome: "SHOW_ERRORS" };
  }

  if (typeOfPAF.WOMANOFCP === registerPatient.type) {
    const today = new Date();
    if (
      registerPatient.continueWithPregnancyTestDateOutdated !== true &&
      isDispensingMoreThan3DaysBetween2Dates(today, registerPatient.pregnancy_test)
    ) {
      return {
        outcome: "MODAL",
        errorMessage: {
          type: "error",
          modalState: 1,
          hasSecondState: false,
          title: errorMessages.OUTDATED_PREGANACY_TEST_TITLE,
          description: <BrochureWithText type="NEED_VALID_NEGATIVE_TEST" hcp={hcp} />,
          buttonText: "Continue",
        },
      };
    }
  }

  if (typeOfPAF.WOMANOFCP === registerPatient.type) {
    if (
      registerPatient.continueWithPregnancyTestDateIncomplete !== true &&
      registerPatient.pregnancy_test === undefined
    ) {
      return {
        outcome: "MODAL",
        errorMessage: {
          type: "error",
          modalState: 1,
          hasSecondState: false,
          title: errorMessages.PREGNANACY_TEST_DATE_INCOMPLETE_TITLE,
          description: (
            <BrochureWithText type="NEED_VALID_NEGATIVE_TEST_INCOMPLETE" hcp={hcp} />
          ),
          buttonText: "Continue",
        },
      };
    }
  }

  if (
    registerPatient.continueWithDateOfBirthUnderEighteen !== true &&
    getAge(registerPatient.patient_dob) < 18
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: errorMessages.OUTDATED_DATE_BIRTH_TITLE,
        description: <BrochureWithText type="NEED_VALID_DATE_BIRTH" hcp={hcp} />,
        buttonText: "Continue",
      },
    };
  }

  if (
    confirmation_risk_patient !== true &&
    registerPatient.continueWithoutConfirmation !== true &&
    registerPatient.type !== typeOfPAF.WOMANOFNCP &&
    Boolean(prescriptionField.prescription_confirmation_risks.length)
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: errorMessages.CONFIRMATION_NOT_PROVIDED_TITLE,
        description: (
          <BrochureWithText
            type="CONFIRMATION_NOT_PROVIDED_DESCRIPTION"
            hcp={hcp}
            confirmationRiskLength={
              prescriptionField?.prescription_confirmation_risks?.length
            }
          />
        ),
        buttonText: "Continue",
      },
    };
  }

  if (
    registerPatient?.continueOffLabel !== true &&
    registerPatient?.indication?.is_indication_other === true
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: "Continue with off-label use",
        description: <BrochureWithText type="CONTINUE_OFF_LABEL" hcp={hcp} />,
        buttonText: "Continue",
      },
    };
  }

  if (
    registerPatient?.continueIndicationIncomplete !== true &&
    (registerPatient?.indication?.label === undefined ||
      registerPatient?.indication?.label === null ||
      registerPatient?.indication?.label === "None")
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: errorMessages.INDICATION_INCOMPLETE_TITLE,
        description: (
          <BrochureWithText type="CONTINUE_INDICATION_INCOMPLETE" hcp={hcp} />
        ),
        buttonText: "Continue",
      },
    };
  }

  if (
    (scheduleDisplayType === "cycle" || scheduleDisplayType === "weeks_per_cycle") &&
    registerPatient?.continueCapsulePerCycleIncomplete !== true &&
    !isNotSelectedAnyCapsulePerCycle(registerPatient)
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: errorMessages.CAPSULES_PER_CYCLE_INCOMPLETE,
        description: (
          <BrochureWithText
            type="CONTINUE_CAPSULE_PER_CYCLE_INCOMPLETE"
            hcp={hcp}
            scheduleDisplayType={prescriptionField?.schedule_display_type}
          />
        ),
        buttonText: "Continue",
      },
    };
  }

  if (
    scheduleDisplayType === "weeks" &&
    registerPatient?.continueCapsulePerWeekIncomplete !== true &&
    !isNotSelectedAnyCapsulePerCycle(registerPatient)
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: errorMessages.CAPSULES_PER_WEEKS_INCOMPLETE,
        description: (
          <BrochureWithText
            type="CONTINUE_CAPSULE_PER_CYCLE_INCOMPLETE"
            hcp={hcp}
            scheduleDisplayType={prescriptionField?.schedule_display_type}
          />
        ),
        buttonText: "Continue",
      },
    };
  }

  if (
    (scheduleDisplayType === "cycle" || scheduleDisplayType === "weeks_per_cycle") &&
    registerPatient?.continueNumberOfCyclesIncomplete !== true &&
    (registerPatient?.number_cycles?.value === 0 ||
      !registerPatient?.number_cycles ||
      !registerPatient?.number_cycles.id)
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: errorMessages.NUMBER_OF_CYCLES_INCOMPLETE,
        description: (
          <BrochureWithText type="CONTINUE_NUMBER_OF_CYCLES_INCOMPLETE" hcp={hcp} />
        ),
        buttonText: "Continue",
      },
    };
  }

  if (
    registerPatient?.continueNumberOfWeeksIncomplete !== true &&
    ((scheduleDisplayType === "weeks_per_cycle" &&
      (!registerPatient?.weeks_per_cycle ||
        registerPatient.weeks_per_cycle.label === "None")) ||
      (scheduleDisplayType === "weeks" &&
        (!registerPatient?.number_weeks || registerPatient?.number_weeks.label === "None")))
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: errorMessages.NUMBER_OF_WEEKS_INCOMPLETE,
        description: (
          <BrochureWithText type="CONTINUE_NUMBER_OF_WEEKS_INCOMPLETE" hcp={hcp} />
        ),
        buttonText: "Continue",
      },
    };
  }

  const isNumberOfCyclesExceeded = checkIfNumberOfCyclesExceeded(registerPatient);

  if (
    scheduleDisplayType === "cycle" &&
    registerPatient.continueMaximumNumberOfCyclesExceeded !== true &&
    typeOfPAF.WOMANOFCP === registerPatient.type &&
    isNumberOfCyclesExceeded
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: "Maximum Number of Cycles Exceeded",
        description: <BrochureWithText type="CYCLES_EXCEEDED_WCBP" hcp={hcp} />,
        buttonText: "Continue",
      },
    };
  }

  if (
    scheduleDisplayType === "weeks" &&
    registerPatient.continueMaximumNumberOfCapsulesExceeded !== true &&
    (typeOfPAF.WOMANOFCP === registerPatient.type
      ? !!prescriptionField.max_capsules_wcbp
      : !!prescriptionField.max_capsules_m_or_wncbp) &&
    (registerPatient.total_capsules !== 0
      ? registerPatient.total_capsules
      : registerPatient.capsule_strength) >
      (typeOfPAF.WOMANOFCP === registerPatient.type
        ? prescriptionField.max_capsules_wcbp
        : prescriptionField.max_capsules_m_or_wncbp)
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: "Capsules outside permitted Quantity",
        code: "MAX_CAPSULES_EXCEEDED",
        description: (
          <BrochureWithText
            type="CAPSULES_EXCEEDED"
            typeOfPAF={typeOfPAF}
            registerPatientType={registerPatient.type}
            maxCapsulesOrWeeks={prescriptionField}
            hcp={hcp}
          />
        ),
        buttonText: "Continue",
      },
    };
  }

  const isWeekSchedule =
    scheduleDisplayType === "weeks" || scheduleDisplayType === "weeks_per_cycle";

  const weeksValue =
    scheduleDisplayType === "weeks_per_cycle"
      ? registerPatient?.total_weeks_supply
      : registerPatient?.other
        ? registerPatient?.other
        : registerPatient?.number_weeks?.id;

  const maxWeeks =
    typeOfPAF.WOMANOFCP === registerPatient.type
      ? prescriptionField.max_weeks_wcbp
      : prescriptionField.max_weeks_m_or_wncbp;

  if (
    isWeekSchedule &&
    maxWeeks &&
    weeksValue > maxWeeks &&
    registerPatient.continueMaximumNumberOfWeeksExceeded !== true
  ) {
    return {
      outcome: "MODAL",
      errorMessage: {
        type: "error",
        modalState: 1,
        hasSecondState: false,
        title: "Maximum number of weeks exceeded",
        code: "MAX_WEEKS_EXCEEDED",
        description: (
          <BrochureWithText
            type="WEEKS_EXCEEDED"
            typeOfPAF={typeOfPAF}
            registerPatientType={registerPatient.type}
            maxCapsulesOrWeeks={prescriptionField}
            hcp={hcp}
          />
        ),
        buttonText: "Continue",
      },
    };
  }

  return { outcome: "NEXT" };
}
